import React, { FC, memo, ReactNode, useState } from 'react';
import { Button, message, Modal, Tooltip } from 'antd';
import { ButtonProps } from 'antd/lib/button';
import { BusinessPartnersAPI } from 'api/BusinessPartnersAPI';
import Column from 'antd/lib/table/Column';
import { initialPageParams } from 'models/page';
import { useMutation, useQuery } from 'react-query';
import { QueryKey } from 'api/queryKey';
import { PartnerHigoDevice } from 'models/higoDevice';
import { ProfileOwner } from 'models/user';
import { PlusSquareOutlined } from '@ant-design/icons';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { NumberedTable } from 'common/components/NumberedTable';
import { BusinessPartnersDevicesModalFilters } from 'pages/BusinessPartnerManagement/BusinessPartnerDevicesManagement/BusinessPartnersDevicesModalFilters';
import { purge } from 'lib/ramda/purge';
import { useRole } from 'common/hooks/useRole';
import { BusinessPartnerContext } from 'pages/BusinessPartnerManagement/BusinessPartnerContext';
import { useEnsureContext } from 'common/hooks';
import { isNil } from 'ramda';

export interface AssignPartnerDeviceToUserButtonProps
  extends Omit<ButtonProps, 'onClick' | 'loading' | 'href'> {
  businessPartnerId: number;
  businessPartnerName: string;
  user: ProfileOwner;
  tooltip?: ReactNode;
  onAssign?: () => void;
}

export const AssignPartnerDeviceToUserButton: FC<AssignPartnerDeviceToUserButtonProps> = memo(
  ({
    businessPartnerId,
    businessPartnerName,
    user,
    onAssign,
    tooltip = 'Assign device to user',
    ...buttonProps
  }) => {
    const [modalVisible, setModalVisible] = useState(false);
    const [selectedDevice, setSelectedDevice] = useState<PartnerHigoDevice>();

    const [pageMetaData, setPageMetaData] = useState(initialPageParams);
    const [filters, setFilters] = useState({});

    const [mutate, { isLoading }] = useMutation(
      BusinessPartnersAPI.assignDeviceToUser
    );
    const bpContext = useEnsureContext(BusinessPartnerContext);

    const { isMedicalOperator } = useRole();
    const shouldHidePartnerColumn =
      !isNil(bpContext.parentPartnerId) || isMedicalOperator;
    const intlMessage = useFormatMessage();

    const assignSelectedDevice = () => {
      selectedDevice &&
        mutate(
          {
            businessPartnerId,
            deviceId: selectedDevice.id, // device id is rowKey
            userId: user.userId,
          },
          {
            onSuccess: () => {
              message.success(
                intlMessage(
                  { id: 'devices.assign.success' },
                  {
                    serialNumber: selectedDevice.serialNumber,
                    firstName: user.firstName,
                    lastName: user.lastName,
                  }
                )
              );
              onAssign && onAssign();
              setModalVisible(false);
            },
          }
        );
    };

    const { isFetching, data, refetch } = useQuery(
      [
        QueryKey.SelectUnassignedBusinessPartnerDevicesList,
        pageMetaData,
        filters,
      ],
      () =>
        BusinessPartnersAPI.fetchDevices(businessPartnerId, {
          ...pageMetaData,
          ...filters,
          modeType: 'FAMILY_MODE',
          partnerName: businessPartnerName,
          assigned: false,
        }),
      {
        keepPreviousData: false,
      }
    );

    const paginationConfig = {
      current: pageMetaData?.page,
      pageSize: data?.size,
      total: data?.totalElements,
      onChange: (page: number, pageSize?: number) =>
        setPageMetaData((prev) => ({
          ...prev,
          page,
          ...(pageSize && { pageSize }),
        })),
    };

    const button = (
      <Button
        onClick={() => {
          refetch();
          setModalVisible(true);
        }}
        icon={<PlusSquareOutlined />}
        {...buttonProps}
      />
    );

    return (
      <>
        {tooltip ? <Tooltip title={tooltip}>{button}</Tooltip> : button}

        <Modal
          visible={modalVisible}
          centered={true}
          width={'50%'}
          title={intlMessage(
            { id: 'familyUsers.selectDevice' },
            {
              firstName: user.firstName,
              lastName: user.lastName,
            }
          )}
          okText={intlMessage({ id: 'devices.assignSelected' })}
          cancelText={intlMessage({ id: 'cancel' })}
          okButtonProps={{ disabled: selectedDevice === undefined }}
          keyboard={false}
          maskClosable={false}
          onCancel={() => setModalVisible(false)}
          onOk={assignSelectedDevice}
          destroyOnClose={true}
          confirmLoading={isLoading}
          closable={!isLoading}
          cancelButtonProps={{ disabled: isLoading }}
        >
          <BusinessPartnersDevicesModalFilters
            style={{ marginBottom: '1rem' }}
            partnerHardcoded={businessPartnerName}
            onSubmit={(values) => {
              setPageMetaData((pageMetaData) => ({ ...pageMetaData, page: 1 }));
              setFilters(purge(values));
            }}
          />

          <NumberedTable
            rowKey="id"
            size="middle"
            loading={isFetching}
            dataSource={data?.content}
            pagination={paginationConfig}
            rowSelection={{
              type: 'radio',
              selectedRowKeys: selectedDevice ? [selectedDevice.id] : [],
              onSelect: (record) => setSelectedDevice(record),
            }}
            onRow={(record) => ({
              onClick: () => {
                setSelectedDevice(record);
              },
            })}
            rowClassName="ant-table-row-interactive"
          >
            {!shouldHidePartnerColumn && (
              <Column
                title={intlMessage({ id: 'partner' })}
                dataIndex={['partnerDetails', 'name']}
                align={'right'}
              />
            )}
            <Column
              title={intlMessage({ id: 'deviceSerialNumber' })}
              align={'right'}
              dataIndex="serialNumber"
            />
          </NumberedTable>
        </Modal>
      </>
    );
  }
);
